import { Component, ChangeDetectionStrategy, OnDestroy, AfterViewInit, ComponentRef, Inject } from '@angular/core'
import { CollectionFacade, isPerson } from '../../collection.facade'
import {
  shareReplay,
  map,
  takeUntil,
  filter,
  distinctUntilChanged,
  withLatestFrom,
  take,
  switchMap,
  tap
} from 'rxjs/operators'
import { Collection, CollectionEntityType } from 'src/io/models/collection'
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject } from 'rxjs'
import { NodeService } from '../../../shared/services/node.service'
import { INode } from '../../../shared/models/node.model'
import {
  CollectionHeaderComponentProps,
  TabComponentProps
} from '../../../libs/collections/components/collection-header/collection-header.interface'
import { createResponsiveHeroImage } from './collection-view.utility'
import { AuthService } from 'src/app/singleton-services/auth/auth.service'
import { WINDOW } from '../../../app.injections'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { CollectionsActionType } from '../../collections.models'
import { RouterService } from 'src/app/singleton-services/router.service'
import { AdConfig, AdTargeting, EqAdTargeting } from '../../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_INTERSTITIAL_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_RECTANGLE_SMALL_SIZE,
  AD_SKIN_SIZE
} from '../../../shared/ads/ads.utility'
import { AdComponentInputs } from '../../../shared/ads/ad/ad-component.interface'
import { environment } from '../../../../environments/environment'
import { AdComponent } from '../../../shared/ads/ad/ad.component'
import {
  mapToAdTargeting,
  UpdateContentTargetingService
} from '../../../shared/ads/advertising-service/update-content-targeting.service'
import { LOGGER_SERVICE } from '../../../logger/logger.config'
import { LoggerService } from '../../../logger/logger.interface'
import {
  buildCanonicalUrl,
  buildPersonSchema,
  CANONICAL_ID,
  STRUCTURED_SCHEMA_ID
} from '../../../singleton-services/seo/seo.utility'
import { CollectionFavoriteService } from 'src/app/libs/collections/components/collection-cta/collection-favorite.service'
import {
  getCategoryTrueId,
  getCategoryTrueType
} from '../../../libs/collections/components/collection-cta/collection-favorite.utility'
import { UpdateHeadService } from 'src/app/singleton-services/seo/update-head.service'

export type CollectionViewComponentProps = CollectionHeaderComponentProps & {
  collectionType: CollectionEntityType | 'contentcategory' | undefined
  collectionId: number | undefined
}

type AdPosition =
  | 'skin'
  | 'leaderboard-lg'
  | 'leaderboard-sm'
  | 'rectangle-1'
  | 'rectangle-mobile'
  | 'adhesion'
  | 'rectangle-sticky'
  | 'interstitial'

/**
 * Handle application level interactions (routing, metadata, etc) for a single
 * collection view and translate data from collection facade to child component props
 * @deprecated
 * New bff-powered entity collections/hub initiative will replace this component and the collection facade.
 * See @EntityHubComponent
 */
@Component({
  selector: 'flo-collection-view',
  templateUrl: './collection-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionViewComponent implements AfterViewInit, OnDestroy {
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly ngAfterViewInit$ = new Subject<void>()
  private readonly loadedAds$ = new BehaviorSubject<AdComponent[]>([])

  constructor(
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly collectionFacade: CollectionFacade,
    private readonly nodeService: NodeService,
    private readonly authService: AuthService,
    private readonly platformService: PlatformService,
    private readonly routerService: RouterService,
    private readonly updateContentTargeting: UpdateContentTargetingService,
    private readonly collectionFavoriteService: CollectionFavoriteService,
    private readonly headService: UpdateHeadService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  /**
   * pass actions from child components to CollectionFacade
   */
  public readonly action$ = this.collectionFacade.action$

  private readonly user$ = this.authService.user$

  public readonly collectionComponentProps$: Observable<CollectionViewComponentProps> = combineLatest([
    this.collectionFacade.collection$,
    this.collectionFacade.tabs$,
    this.user$,
    this.collectionFacade.statxSubHeader$
  ]).pipe(
    map(([collection, tabs, user, statxSubHeader]) => ({
      isUserPremium: user.isPremium,
      isUserLoggedIn: user.loggedIn,
      ...getCollectionComponentProps(collection, tabs, statxSubHeader)
    })),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  )

  public readonly tabs$ = this.collectionComponentProps$.pipe(
    map(props => props.tabs),
    filter(tabs => !!tabs)
  )

  public readonly cta$ = this.collectionComponentProps$.pipe(
    switchMap(props => {
      const propType = getCategoryTrueType(props) ?? null
      const propId = getCategoryTrueId(props) ?? 0
      return this.collectionFavoriteService.getCtaType(
        props.isUserPremium || false,
        props.isUserLoggedIn || false,
        propType,
        propId,
        props.categories
      )
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  )

  private readonly mapMetaProps$ = this.collectionFacade.collection$.pipe(
    map(collection => ({
      title: collection.meta_collections_page_title || collection.title,
      seo_description: collection.description_plain || collection.meta_collections_page_description,
      asset: { url: collection.asset_url }
    })),
    shareReplay(1)
  )

  public nodeListWidgetProps$ = this.collectionFacade.relatedCollections$.pipe(
    filter(nodes => nodes?.data?.length > 0),
    map(nodes => ({ nodes: nodes.data }))
  )

  public readonly updateMeta$ = combineLatest([this.mapMetaProps$, this.collectionFacade.selectedNode$])
    .pipe(
      map(([metaProps, selectedNode]) => (!!selectedNode ? selectedNode : metaProps)),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(collectionProps => this.nodeService.applyNodeMetaToPage(collectionProps as INode))

  private readonly videoEnded$ = this.action$.pipe(
    filter(action => action.type === CollectionsActionType.VideoEndedEvent)
  )

  public readonly scrollToTop$ = this.collectionFacade.collectionIdentifier$
    .pipe(
      distinctUntilChanged(
        (
          a: { collectionId?: string; collectionTag?: string; nodeId?: string },
          b: { collectionId?: string; collectionTag?: string; nodeId?: string }
        ) =>
          (!!b.collectionId && b.collectionId === a.collectionId) ||
          (!!b.collectionTag && b.collectionTag === a.collectionTag) ||
          (!!b.nodeId && b.nodeId === a.nodeId)
      ),
      filter(() => this.platformService.isBrowser)
    )
    .subscribe(() => {
      this.window.scroll(0, 0)
    })

  public readonly goToNextVideo = this.videoEnded$
    .pipe(
      withLatestFrom(
        this.collectionFacade.nodes$,
        this.collectionFacade.selectedNode$.pipe(map(node => (node || { id: undefined }).id)),
        this.routerService.queryParams$
      )
    )
    .subscribe(([videoEnded, nodes, selectedNodeId, queryParams]) => {
      const currentIndex = nodes.findIndex(node => `${node.id}` === `${selectedNodeId}`)
      const nextIndex = currentIndex < nodes.length ? currentIndex + 1 : 0
      const playing = nodes[nextIndex].id
      this.routerService.router.navigate([], {
        queryParamsHandling: 'merge',
        queryParams: {
          playing
        }
      })
    })

  public ngAfterViewInit(): void {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy(): void {
    this.headService.removeElement(CANONICAL_ID)
    this.headService.removeElement(STRUCTURED_SCHEMA_ID)
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  private readonly updatePageHead = this.ngAfterViewInit$
    .pipe(
      switchMap(_ => {
        return this.collectionFacade.nodeWithShareableLink$.pipe(
          tap(node => {
            this.headService.addElementToHead(buildCanonicalUrl(node.shareable_link))
            if (isPerson(node)) {
              const personSchemaElement = buildPersonSchema(node)
              this.headService.addElementToHead(personSchemaElement)
            }
          })
        )
      }),
      take(1)
    )
    .subscribe()

  // Ads
  public readonly AdComponent = import('../../../shared/ads/ad/ad.component').then(m => m.AdComponent)

  private readonly adTargeting$: Observable<AdTargeting> = combineLatest([
    this.collectionFacade.collection$,
    this.user$,
    this.updateContentTargeting.nestedContentTargeting$
  ]).pipe(
    map(([collection, user, nestedContentTargeting]) => {
      return {
        ...mapToAdTargeting(collection, nestedContentTargeting, user.isPremium),
        analyticsTracking: collection
      }
    }),
    distinctUntilChanged(EqAdTargeting.equals)
  )

  private refreshAds$ = this.adTargeting$
    .pipe(
      withLatestFrom(this.loadedAds$),
      switchMap(args => {
        this.logger.trace('Collections View: updating ad targeting')

        const [targeting, loadedAds] = args
        return forkJoin(loadedAds.map(ad => ad.updateTargeting(targeting))).pipe(map(_ => args))
      }),
      switchMap(([targeting, loadedAds]) => {
        this.logger.trace('Collections View: video change refresh triggered', {
          targeting
        })
        return forkJoin(loadedAds.map(ad => ad.refresh()))
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public readonly loadedAdsPush = (loadedAd: ComponentRef<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  public readonly initialAdConfigMap$: Observable<Map<AdPosition, AdComponentInputs>> = this.adTargeting$.pipe(
    take(1),
    map(targeting => {
      const config = new Map<AdPosition, AdComponentInputs>()

      config.set('skin', {
        config: {
          adConfig: AdConfig('collections/atf', AD_SKIN_SIZE, 'collection-skin', targeting)
        }
      })

      config.set('leaderboard-lg', {
        config: {
          adConfig: AdConfig('collections/728x90_flex', AD_LEADERBOARD_LARGE_FLEX_SIZE, 'leaderboard-large', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      config.set('leaderboard-sm', {
        config: {
          adConfig: AdConfig('collections/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small', targeting)
        }
      })

      config.set('rectangle-1', {
        config: {
          adConfig: AdConfig('collections/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'rectangle-1', targeting)
        }
      })

      config.set('rectangle-sticky', {
        config: {
          adConfig: AdConfig('collections/300x250_2', AD_RECTANGLE_LARGE_SIZE, 'rectangle-sticky', targeting),
          additionalOpts: { maxRefreshTime: 60000 }
        }
      })

      // Ads team requests this never refreshes
      config.set('rectangle-mobile', {
        config: {
          adConfig: AdConfig('collections/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'rectangle-mobile', targeting)
        }
      })

      config.set('adhesion', {
        config: {
          adConfig: AdConfig('collections/adhesion', AD_ADHESION_SIZE, 'adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      config.set('interstitial', {
        config: {
          adConfig: AdConfig('collections/interstitial', AD_INTERSTITIAL_SIZE, 'interstitial', targeting)
        }
      })

      return config
    })
  )

  // End Ads
}

const getCollectionComponentProps = (
  collection: Collection,
  tabs: ReadonlyArray<TabComponentProps>,
  statxSubHeader?: string
): CollectionViewComponentProps => ({
  ...mapCollectionToProps(collection),
  subheaderText: statxSubHeader || collection.subtitle || undefined,
  tabs
})

export function mapCollectionToProps(collection: Collection): CollectionViewComponentProps {
  return {
    ...collection,
    tabs: [], // Tabs mapped in getCollectionComponentProps
    heroImage: createResponsiveHeroImage(collection),
    headerText: collection.short_title || collection.title || undefined,
    subheaderText: collection.subtitle || undefined,
    bodyText: collection.description || undefined,
    collectionType: getCategoryTrueType(collection),
    collectionId: getCategoryTrueId(collection)
  }
}
